import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { AuthorizedFilesService } from '@cl/core/services/authorized-files.service';
import { CommandResult } from '@cl/shared/models';
import { FileMetadata } from '@cl/shared/models/file.model';
import { defer, finalize, Subscription, switchMap } from 'rxjs';

@Component({
    selector: 'cl-file-viewer',
    standalone: false,
    templateUrl: './file-viewer.component.html',
    styleUrl: './file-viewer.component.less',
})
export class FileViewerComponent implements OnChanges, OnDestroy {
    @Input() fileName: string;
    @Input() fileIdentity?: string;
    @Input() loadFileMetadata: () => Promise<CommandResult<FileMetadata>>;

    previewLoading = false;
    fileErrors: string[] = [];
    previewUrl: SafeResourceUrl | null = null;
    imageUrl: string | null = null;
    private blobUrl: string | null = null;
    private loadSubscription?: Subscription;

    constructor(
        private authorizedFiles: AuthorizedFilesService,
        private sanitizer: DomSanitizer,
    ) {}

    get canPreview(): boolean {
        return /\.(pdf|png|jpe?g)$/i.test(this.fileName || '');
    }

    ngOnChanges(changes: SimpleChanges): void {
        // A template may pass a new loadFileMetadata function on every change detection; only another file reloads the preview.
        if (changes.fileName || changes.fileIdentity) {
            this.openFile();
        }
    }

    ngOnDestroy(): void {
        this.clearPreview();
    }

    imageFailed(event: Event): void {
        if ((event.target as HTMLImageElement).src !== this.imageUrl) {
            return;
        }

        this.clearPreview();
        this.fileErrors = ['Attēlu neizdevās atvērt. Izmantojiet esošo lejupielādes iespēju.'];
    }

    private openFile(): void {
        this.clearPreview();
        this.fileErrors = [];
        if (!this.canPreview) {
            return;
        }

        this.previewLoading = true;
        this.loadSubscription = defer(() => this.loadFileMetadata())
            .pipe(
                switchMap((result) => {
                    if (result?.errors?.length || !result?.data) {
                        throw { error: result };
                    }

                    return this.authorizedFiles.getFile(result.data.id);
                }),
                finalize(() => (this.previewLoading = false)),
            )
            .subscribe({
                next: (bytes) => {
                    // Only PDF and raster images from the existing authorized route become a local preview URL.
                    if (!['application/pdf', 'image/png', 'image/jpeg'].includes(bytes.type)) {
                        this.fileErrors = ['Šim formātam priekšskats nav pieejams. Izmantojiet esošo lejupielādes iespēju.'];
                        return;
                    }

                    try {
                        this.blobUrl = URL.createObjectURL(bytes);
                        if (bytes.type === 'application/pdf') {
                            this.previewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.blobUrl);
                        } else {
                            this.imageUrl = this.blobUrl;
                        }
                    } catch (error: any) {
                        this.clearPreview();
                        this.fileErrors = this.errors(error, 'Failu neizdevās atvērt.');
                    }
                },
                error: (error: any) => {
                    this.fileErrors = this.errors(error, 'Failu neizdevās atvērt.');
                },
            });
    }

    private clearPreview(): void {
        // Unsubscribing drops a pending metadata result and aborts the file request, so a late answer cannot replace a newer file.
        this.loadSubscription?.unsubscribe();
        this.previewUrl = null;
        this.imageUrl = null;
        this.previewLoading = false;
        if (this.blobUrl) {
            URL.revokeObjectURL(this.blobUrl);
            this.blobUrl = null;
        }
    }

    private errors(error: any, fallback: string): string[] {
        const errors = error.error?.errors;
        return Array.isArray(errors) && errors.length
            ? errors.map((message: any) => (typeof message === 'string' ? message : message.error || fallback))
            : [fallback];
    }
}
